/**
 * Recovery for failed lazy-chunk loads.
 *
 * This app is code-split (vite.config: autoCodeSplitting + hashed chunk names)
 * and preloads routes on intent. When a new version is deployed, an app that is
 * still holding the OLD index.html references chunk hashes the CDN no longer has,
 * so the next dynamic import fails with "Failed to fetch dynamically imported
 * module" and the router falls into its generic "Something went wrong" screen.
 * A flaky cold-start fetch produces the same failure.
 *
 * The cure is what the user does by hand — reload to pick up the fresh
 * index.html + hashes. We do it automatically, once per short window, so a
 * genuinely broken deploy can't turn into a reload loop.
 */

import * as Sentry from "@sentry/react";
import { isFlexApp } from "@/hooks/useIsFlexApp";

const RELOAD_TS_KEY = "amber:chunk-reload-ts";
// A real deploy heals in one reload; if we're back here within this window the
// reload didn't help (broken deploy / offline), so we stop and show the message.
const RELOAD_GUARD_MS = 15_000;
let lastReloadTs = 0;

/** True if `error` looks like a failed dynamic import / chunk load. */
export function isChunkLoadError(error: unknown): boolean {
	if (!error) return false;
	const message = (
		error instanceof Error ? error.message : String(error)
	).toLowerCase();

	return (
		message.includes("dynamically imported module") ||
		message.includes("importing a module script failed") ||
		message.includes("error loading dynamically imported module") ||
		message.includes("chunkloaderror") ||
		// Safari/iOS wording, and the generic fetch failure for a .js chunk.
		message.includes("module script failed") ||
		(message.includes("failed to fetch") && message.includes(".js")) ||
		// The browser rejected the response as a module script because it was
		// not served as JavaScript: the chunk URL answered with a 2xx (or a
		// redirect) whose body is an HTML document, so the strict MIME check
		// fails. NOTE: a plain 404 is NOT worded like this in any engine — S3's
		// HTML NoSuchKey page still yields "Importing a module script failed" in
		// WebKit — so seeing this means something served HTML with a success
		// status. Wording differs per engine:
		//   Safari   'text/html' is not a valid JavaScript MIME type.
		//   Chrome   Failed to load module script: Expected a JavaScript(-or-Wasm)
		//            module script but the server responded with a MIME type of
		//            "text/html".
		//   Firefox  ... was blocked because of a disallowed MIME type
		//            ("text/html").
		message.includes("is not a valid javascript mime type") ||
		message.includes("expected a javascript module script") ||
		message.includes("disallowed mime type") ||
		(message.includes("mime type") && message.includes("javascript"))
	);
}

interface ChunkResourceDiag {
	name: string;
	status?: number;
	transfer: number;
	encoded: number;
	decoded: number;
	delivery?: string;
	protocol: string;
	initiator: string;
	durationMs: number;
}

/**
 * Report a chunk-load failure to Sentry together with what the browser actually
 * received for every /static/*.js request in this page's lifetime.
 *
 * Safari's error text does not include the failing URL, so this is how we find
 * out which chunk came back as HTML, its status (where the browser exposes it),
 * whether it was served from a cache or a service worker, and its body size —
 * an index.html body is a few KB where a chunk is tens to hundreds of KB.
 *
 * Fire-and-forget, called BEFORE any reload; Sentry's transport uses keepalive
 * so the event survives the navigation. Never throws.
 */
export function reportChunkLoadError(error: unknown, source: string): void {
	try {
		const resources: ChunkResourceDiag[] = performance
			.getEntriesByType("resource")
			.filter((e) => /\/static\/[^?]*\.js/.test(e.name))
			.map((e) => {
				const r = e as PerformanceResourceTiming & {
					responseStatus?: number;
					deliveryType?: string;
				};
				return {
					name: r.name.replace(window.location.origin, ""),
					status: r.responseStatus,
					transfer: r.transferSize,
					encoded: r.encodedBodySize,
					decoded: r.decodedBodySize,
					delivery: r.deliveryType,
					protocol: r.nextHopProtocol,
					initiator: r.initiatorType,
					durationMs: Math.round(r.duration),
				};
			});
		// A .js URL that came back as index.html has a body the size of the HTML
		// document, so the smallest non-empty bodies are the prime suspects.
		const suspicious = [...resources]
			.filter((r) => r.decoded > 0 && r.decoded < 20_000)
			.sort((a, b) => a.decoded - b.decoded)
			.slice(0, 10);

		Sentry.captureException(error, {
			level: "warning",
			tags: { chunk_load_error: "true", chunk_error_source: source },
			extra: {
				entryScript: document.querySelector<HTMLScriptElement>(
					'script[type="module"][src]',
				)?.src,
				resourceCount: resources.length,
				suspiciousResources: suspicious,
				resources: resources.slice(-40),
				serviceWorkerControlled: Boolean(navigator.serviceWorker?.controller),
				hasWafToken: document.cookie.includes("aws-waf-token"),
				online: navigator.onLine,
				isFlexApp: isFlexApp(),
				flexAppVersion: window.flexAppVersion,
				flexAppPlatform: window.flexAppPlatform,
			},
		});
		console.warn("[chunkReload] chunk load error", source, error, suspicious);
	} catch {
		// Diagnostics must never get in the way of recovery.
	}
}

/**
 * Reload once to recover from a stale/failed chunk. Guarded by a timestamp so a
 * persistently failing load doesn't loop. Returns true if a reload was started
 * (caller should render a neutral loader and stop).
 */
export function reloadForChunkError(): boolean {
	if (typeof window === "undefined") return false;

	let last = lastReloadTs;
	try {
		const stored = Number(window.sessionStorage.getItem(RELOAD_TS_KEY) || 0);
		// Use whichever is more recent — covers both the storage and in-memory paths
		last = Math.max(last, stored);
	} catch {
		// sessionStorage blocked (private mode / webview) — use in-memory fallback
	}

	if (Date.now() - last < RELOAD_GUARD_MS) {
		// Already reloaded very recently and still failing — give up, let the
		// caller show the manual "refresh" message instead of looping.
		return false;
	}

	lastReloadTs = Date.now();
	try {
		window.sessionStorage.setItem(RELOAD_TS_KEY, String(lastReloadTs));
	} catch {
		// best effort — in-memory guard is already set above
	}

	hardReload();
	return true;
}

/**
 * Reload in a way that cannot re-serve the *stale* index.html.
 *
 * `location.reload()` is allowed to answer the document from the HTTP cache.
 * Since our index.html ships without a Cache-Control header, browsers apply
 * heuristic freshness (~10% of the Last-Modified age), so a plain reload can
 * hand back the very same HTML — with the very same dead chunk hashes — and the
 * user sees the error again. Re-fetching with `cache: "reload"` forces a
 * network round trip and rewrites the cache entry, so the reload that follows
 * gets the current HTML.
 *
 * Note this only defeats the *browser* cache. A CDN edge holding a stale
 * index.html must be fixed at the origin (Cache-Control: no-cache on HTML).
 */
export function hardReload(): void {
	const done = () => window.location.reload();
	try {
		fetch(window.location.href, {
			cache: "reload",
			credentials: "same-origin",
		})
			.then(done)
			// Offline or blocked — reload anyway, it is still the best move.
			.catch(done);
	} catch {
		done();
	}
}
